// Trang quản lý tài khoản admin / nhân viên — chỉ admin
import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Tag, Space, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, TeamOutlined, SafetyOutlined } from '@ant-design/icons';
import api, { errorMessage, ROLES, ROLE_LABELS } from '../api';

const ROLE_OPTIONS = [
  { value: ROLES.STAFF, label: `${ROLE_LABELS.staff} — chỉ xem và duyệt đơn` },
  { value: ROLES.ADMIN, label: `${ROLE_LABELS.admin} — toàn quyền` },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Hoạt động' },
  { value: 'inactive', label: 'Đã khóa' },
];

const formatDate = (v) => (v ? new Date(v).toLocaleString('vi-VN') : '-');

function Users({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = đóng, {} = tạo mới, user = sửa
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const isNew = editing && !editing.id;
  const isSelf = editing?.id === currentUser?.id;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data?.data || []);
    } catch (err) {
      message.error(errorMessage(err, 'Không tải được danh sách tài khoản.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const openEditor = (user) => {
    setEditing(user || {});
    form.setFieldsValue(
      user
        ? { full_name: user.full_name, role: user.role, status: user.status, password: '' }
        : { username: '', password: '', full_name: '', role: ROLES.STAFF, status: 'active' }
    );
  };

  const closeEditor = () => {
    setEditing(null);
    form.resetFields();
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/users', values);
        message.success('Đã tạo tài khoản.');
      } else {
        await api.put(`/users/${editing.id}`, values);
        message.success('Đã cập nhật tài khoản.');
      }
      closeEditor();
      fetchUsers();
    } catch (err) {
      message.error(errorMessage(err, 'Lưu tài khoản thất bại.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset2fa = async (user) => {
    try {
      const res = await api.post(`/users/${user.id}/reset-2fa`);
      message.success(res.data?.message || 'Đã reset 2FA.');
      fetchUsers();
    } catch (err) {
      message.error(errorMessage(err, 'Reset 2FA thất bại.'));
    }
  };

  const columns = [
    { title: 'Tên đăng nhập', dataIndex: 'username', key: 'username', render: (v) => <strong>{v}</strong> },
    { title: 'Họ tên', dataIndex: 'full_name', key: 'full_name' },
    {
      title: 'Quyền',
      dataIndex: 'role',
      key: 'role',
      render: (v) => <Tag color={v === ROLES.ADMIN ? 'red' : 'blue'}>{ROLE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (v) => (v === 'active' ? <Tag color="green">Hoạt động</Tag> : <Tag>Đã khóa</Tag>),
    },
    {
      title: '2FA',
      dataIndex: 'totp_enabled',
      key: 'totp_enabled',
      render: (v) => (v ? <Tag color="green">Đã bật</Tag> : <Tag color="orange">Chưa quét QR</Tag>),
    },
    { title: 'Đăng nhập gần nhất', dataIndex: 'last_login', key: 'last_login', render: formatDate },
    { title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', render: formatDate },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      render: (_, user) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(user)}>Sửa</Button>
          {!!user.totp_enabled && (
            <Popconfirm
              title={`Reset 2FA của ${user.username}?`}
              description={
                user.id === currentUser?.id
                  ? 'Bạn sẽ bị đăng xuất và phải quét mã QR mới.'
                  : 'Tài khoản bị đăng xuất, lần sau phải quét mã QR mới.'
              }
              onConfirm={() => handleReset2fa(user)}
              okText="Reset"
              cancelText="Hủy"
            >
              <Button size="small" danger icon={<SafetyOutlined />}>Reset 2FA</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<Space><TeamOutlined />Quản lý tài khoản</Space>}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openEditor(null)}>Tạo tài khoản</Button>}
      style={{ background: '#FFF5EE' }}
    >
      <Table rowKey="id" columns={columns} dataSource={users} loading={loading} size="small" pagination={false} />

      <Modal
        open={editing !== null}
        title={isNew ? 'Tạo tài khoản' : `Sửa tài khoản ${editing?.username || ''}`}
        onOk={handleSave}
        onCancel={closeEditor}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {isNew && (
            <Form.Item
              name="username"
              label="Tên đăng nhập"
              rules={[
                { required: true, message: 'Nhập tên đăng nhập' },
                { pattern: /^[a-zA-Z0-9_.]{3,50}$/, message: '3-50 ký tự, chỉ gồm chữ, số, "_" hoặc "."' },
              ]}
            >
              <Input autoComplete="off" />
            </Form.Item>
          )}
          <Form.Item
            name="password"
            label={isNew ? 'Mật khẩu' : 'Mật khẩu mới (để trống nếu không đổi)'}
            rules={[
              { required: isNew, message: 'Nhập mật khẩu' },
              { min: 6, message: 'Ít nhất 6 ký tự' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="full_name" label="Họ tên">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="role" label="Quyền" rules={[{ required: true }]}>
            <Select options={ROLE_OPTIONS} disabled={isSelf} />
          </Form.Item>
          {!isNew && (
            <Form.Item name="status" label="Trạng thái" rules={[{ required: true }]}>
              <Select options={STATUS_OPTIONS} disabled={isSelf} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Card>
  );
}

export default Users;
